import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BellRing, Info } from "lucide-react";
import { CredentialProposalStatus } from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTRPC } from "@/trpc";
import { ProviderImportDialog } from "../components/provider-imports/provider-import-dialog";
import { ProviderImportCatalogButton } from "../components/provider-imports/provider-import-catalog-button";
import {
  ProviderOfferFilter,
  type ProviderImportScope,
} from "../components/provider-imports/provider-import-types";
import type { CredentialImportAction } from "../lib/provider-import-context";
import {
  formatProviderUpdateDate,
  latestProviderUpdateAt,
} from "../lib/provider-import-timestamps";

const POPOVER_VIEWPORT_PADDING_PX = 16;

export function useProviderCredentialImports({
  scope,
  enabled,
  canEdit,
  disabled,
}: {
  scope: ProviderImportScope;
  enabled: boolean;
  canEdit: boolean;
  disabled: boolean;
}) {
  const trpc = useTRPC();
  const offers = useQuery({
    ...trpc.credentials.listProviderCredentialOffersForBu.queryOptions({
      ...scope,
      filter: ProviderOfferFilter.All,
    }),
    enabled,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [initialOfferId, setInitialOfferId] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
      setInitialOfferId(null);
    }
  }, [enabled]);
  const latestUpdateAt = latestProviderUpdateAt(offers.data?.offers ?? []);
  const actions = new Map<string, CredentialImportAction>();
  if (enabled) {
    for (const offer of offers.data?.offers ?? []) {
      actions.set(offer.credentialId, {
        offerId: offer.id,
        pending:
          offer.status === CredentialProposalStatus.Pending &&
          offer.reviewRequired &&
          offer.isActionable &&
          !offer.dismissedAt,
      });
    }
  }
  function open(offerId: string | null) {
    if (!enabled || disabled || isOpen) {
      return;
    }
    setInitialOfferId(offerId);
    setIsOpen(true);
  }
  return {
    isOpen: enabled && isOpen,
    context: enabled
      ? {
          actions,
          onReview: (offerId: string) => open(offerId),
          openCatalog: () => open(null),
          disabled,
          isOpen,
        }
      : null,
    action: enabled ? <ProviderImportCatalogButton /> : null,
    notice:
      enabled && !!offers.data?.pendingCount ? (
        <section
          aria-label="Provider updates"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 border-l-4 border-l-primary bg-primary/10 p-4"
        >
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <BellRing className="size-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-1">
                <p
                  aria-live="polite"
                  className="text-base font-bold text-slate-950"
                >
                  {offers.data.pendingCount} new provider{" "}
                  {offers.data.pendingCount === 1 ? "update" : "updates"}
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="About provider updates"
                      className="size-8 shrink-0 rounded-full text-primary/70 hover:bg-primary/10 hover:text-primary"
                    >
                      <Info className="size-4" aria-hidden="true" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    collisionPadding={POPOVER_VIEWPORT_PADDING_PX}
                    aria-label="About provider updates"
                    className="w-80 max-w-[calc(100vw-2rem)] rounded-xl text-sm leading-relaxed text-slate-700"
                  >
                    The provider updated their credentials. Review the changes,
                    then import all updates or choose specific credentials and
                    fields. Your organization’s records stay the same until you
                    import.
                  </PopoverContent>
                </Popover>
              </div>
              {latestUpdateAt && (
                <p className="text-xs text-slate-600">
                  Latest update{" "}
                  <time dateTime={latestUpdateAt}>
                    {formatProviderUpdateDate(latestUpdateAt)}
                  </time>
                </p>
              )}
            </div>
          </div>
          <Button
            type="button"
            className="w-full rounded-xl sm:w-auto"
            disabled={disabled || isOpen}
            onClick={() => open(null)}
          >
            Review updates
          </Button>
        </section>
      ) : null,
    dialog:
      enabled && isOpen ? (
        <ProviderImportDialog
          scope={scope}
          data={offers.data}
          canImport={canEdit && !!offers.data?.canImport}
          initialOfferId={initialOfferId}
          loading={offers.isLoading}
          loadError={offers.error?.message ?? null}
          onClose={() => setIsOpen(false)}
        />
      ) : null,
  };
}
